import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'

import { InstallationResponseDto } from 'src/Libs/e-mobility/shared/api/dto/installation-response.dto'
import { useCallback } from 'react'
import { AxiosError } from 'axios'
import { HagerBridgeResponseErrorFormat } from 'src/Libs/e-mobility/shared'
import { usePortalQuery } from 'src/Libs/api'

/**
 * Fetch a subset of installation data via HagerBridge. Besides some properties from the InstallationModel provided by
 * E3/DC installation service that is used in the majority of the codebase, this response includes some properties specific
 * to the new e-mobility backend(HagerBridge).
 *
 * Most importantly, the property "newEmobilityEnabled" tells us if the migration of installation data from the old backend
 * to the new backend(HagerBridge) is completed and if user is allowed to see the new frontend implementation.
 */
interface UseFetchHagerBridgeInstallation {
  installation: InstallationResponseDto | undefined
  isLoading: boolean
  isError: boolean
  error: (AxiosError & HagerBridgeResponseErrorFormat) | null
  isSuccess: boolean
}

interface UseFetchHagerBridgeInstallationParams {
  installationId: number | undefined
  isInstallationDeleted: boolean
  /**
   * allow hook to get its unique state to prevent other hook instances interfering and resetting its flags(isLoading,
   * isError, isSuccess). This is important for this exact hook because it serves not only in feature components, but
   * also as a "health check" hook at the beginning of the app runtime, therefore it might fail immediately if the backend
   * service is down.
   *
   * For this reason, we allow passing a custom queryKey for the health check instance of the hook, making sure that when
   * it errors, it stays in the error state.
   */
  queryKey?: string
}

export const useFetchHagerBridgeInstallation = ({
  installationId,
  isInstallationDeleted,
  queryKey,
}: UseFetchHagerBridgeInstallationParams): UseFetchHagerBridgeInstallation => {
  const authFetch = useAuthFetchWithResult()

  const fetchInstallation = useCallback(async (): Promise<InstallationResponseDto> => {
    return authFetch<InstallationResponseDto>({
      path: `installations/${installationId}`,
      method: 'GET',
      endpoint: process.env.REACT_APP_HAGER_BRIDGE_SERVICE,
      option: {
        /**
         * In case HagerBridge is incredibly slow for some reason(migration or indexing in progress), we don't
         * want to wait for the default request timeout set to 60 or 120 seconds. Instead, we assume that HagerBridge
         * is unavailable after 15 seconds, throw an error intentionally and fall back to
         * the old e-mobility service and old e-mobility UI.
         */
        timeout: 15_000,
      },
    })
  }, [authFetch, installationId])

  const {
    data: installation,
    isLoading,
    isError,
    isSuccess,
    error,
  } = usePortalQuery<InstallationResponseDto>({
    queryFn: fetchInstallation,
    queryKey: [queryKey || 'hagerBridgeInstallation', installationId],
    refetchOnMount: true,
    retry: false,
    enabled: Boolean(installationId) && !isInstallationDeleted,
  })

  return {
    installation: installation ?? undefined,
    isLoading,
    isError,
    error: error as (AxiosError & HagerBridgeResponseErrorFormat) | null,
    isSuccess,
  }
}
